import { useState, useEffect } from 'react'
import {
  Button,
  Collapse,
  Input,
  Modal,
  Form,
  Divider,
  Spin,
  Select,
} from 'antd'
import {
  EditOutlined,
  AppstoreAddOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { get, save, deleteFaq } from 'src/api/faq'
import './Faq.css'
import { LANGUAGE_NAMES } from 'src/constants'
const { Panel } = Collapse
const { Option } = Select
const { confirm } = Modal

const Faq = () => {
  const [categories, setCategories] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [currentFaq, setCurrentFaq] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [languages] = useState<(keyof typeof LANGUAGE_NAMES)[]>(
    Object.keys(LANGUAGE_NAMES) as (keyof typeof LANGUAGE_NAMES)[]
  )
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [activeLanguage, setActiveLanguage] = useState('en')
  const [rawFaqs, setRawFaqs] = useState([])
  const [modalActiveLanguage, setModalActiveLanguage] = useState('en')
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [categoryForm] = Form.useForm()
  const [categoryActiveLanguage, setCategoryActiveLanguage] = useState('en')
  const [selectedCategoryEdit, setSelectedCategoryEdit] = useState(null)
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false)

  const showEditCategoryModal = (category) => {
    setSelectedCategoryEdit(category.category)
    setEditingCategory(category)
    const initialValues = languages.reduce((acc, lang) => {
      acc[`name_${lang}`] =
        category.faqs[0].category_translations?.[lang]?.name || ''
      return acc
    }, {} as Record<string, string>)

    categoryForm.setFieldsValue(initialValues)
    setCategoryActiveLanguage(activeLanguage)
    setIsCategoryModalVisible(true)
  }

  const handleCategoryOk = () => {
    categoryForm.validateFields().then((values) => {
      if (!editingCategory) return

      const categoryTranslations = languages.reduce((acc, lang) => {
        acc[lang] = { name: values[`name_${lang}`] || '' }
        return acc
      }, {} as Record<string, { name: string }>)

      const payload = {
        category: selectedCategoryEdit,
        type: 'category',
        lang: categoryActiveLanguage,
        title: categoryTranslations[categoryActiveLanguage]?.name,
      }

      setIsModalVisible(false)
      setIsLoading(true)
      save(payload)
        .then(() => {
          fetchData()
        })
        .finally(() => {
          setIsLoading(false)
        })
    })
    setIsCategoryModalVisible(false)
  }

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await get()
      setRawFaqs(response.data)
      setCategories(groupFaqsByCategory(response.data, activeLanguage))
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const groupFaqsByCategory = (faqs, lang) => {
    const grouped = faqs.reduce((acc, faq) => {
      const category = faq.category
      if (!acc[category]) acc[category] = []
      acc[category].push(faq)
      return acc
    }, {})

    return Object.entries(grouped).map(([category, faqs]) => ({
      category,
      title: faqs[0]?.category_translations?.[lang]?.name || category,
      faqs,
    }))
  }

  const showEditModal = (faq) => {
    if (faq == null) {
      setSelectedCategory(categories[0]?.category)
      setIsCategoryDisabled(false)
    } else {
      setSelectedCategory(faq?.category)
      const category = categories.find((cat) => cat.category === faq.category)
      const isOnlyFaqInCategory = category && category.faqs.length === 1
      setIsCategoryDisabled(isOnlyFaqInCategory)
    }
    setCurrentFaq(faq)
    setModalActiveLanguage(activeLanguage)

    if (faq) {
      const initialValues = {
        id: faq.id,
        category: faq.category,
        ...Object.entries(faq.faq_translations).reduce(
          (acc, [lang, translation]) => {
            const t = translation as { name: string; description: string }
            acc[`title_${lang}`] = t.name
            acc[`description_${lang}`] = t.description
            return acc
          },
          {} as Record<string, string>
        ),
      }
      form.setFieldsValue(initialValues)
    } else {
      form.resetFields()
      form.setFieldsValue({ category: categories[0]?.category })
    }
    setIsModalVisible(true)
  }

  const getCategoryById = (categories: any[], id: string) =>
    categories.find((cat) => cat.category === id)

  const handleOk = () => {
    form.validateFields().then((values) => {
      const { id, category, ...translationFields } = values
      const enTitle = translationFields['title_en'] || ''
      const enDescription = translationFields['description_en'] || ''
      const faq_translations = languages.reduce((acc, lang) => {
        const titleKey = `title_${lang}`
        const descKey = `description_${lang}`
        const title = translationFields[titleKey] || enTitle
        const description = translationFields[descKey] || enDescription
        acc[lang] = {
          name: title,
          description: description,
        }
        return acc
      }, {})

      let payload = {}

      const categoryMap = getCategoryById(categories, selectedCategory)?.faqs[0]
        .category_translations
      const changeTitleAndDesc = faq_translations[modalActiveLanguage]
      if (id == null) {
        payload = {
          category: category,
          type: 'faq',
          faqTranslations: faq_translations,
          categoryTranslations: categoryMap,
        }
      } else {
        payload = {
          id: id || undefined,
          category: category,
          type: 'faq',
          description: changeTitleAndDesc.description,
          title: changeTitleAndDesc.name,
          lang: modalActiveLanguage,
        }
      }

      setIsModalVisible(false)
      setIsLoading(true)
      save(payload)
        .then(() => {
          fetchData()
        })
        .finally(() => {
          setIsLoading(false)
        })
    })
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleDelete = (faq) => {
    setIsLoading(true)
    deleteFaq(faq)
      .then(() => {
        fetchData()
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const showDeleteConfirm = (faq) => {
    const title = faq.faq_translations?.[activeLanguage]?.name || 'Untitled FAQ'
    confirm({
      title: `Delete "${title}"?`,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => handleDelete(faq),
    })
  }

  const renderCategoryHeader = (category) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <h3>{category.title}</h3>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={() => showEditCategoryModal(category)}
      />
    </div>
  )

  const renderFaqPanelHeader = (faq) => {
    const title = faq.faq_translations?.[activeLanguage]?.name || 'Untitled FAQ'
    const category = categories.find((cat) => cat.category === faq.category)
    const isOnlyFaqInCategory = category && category.faqs.length === 1

    return (
      <div className="faq-panel-header">
        <span className="faq-title">{title}</span>
        <div className="faq-actions">
          <Button
            type="link"
            size="small"
            className="text-gray-600"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              showEditModal(faq)
            }}
          />
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            disabled={isOnlyFaqInCategory}
            onClick={(e) => {
              e.stopPropagation()
              if (!isOnlyFaqInCategory) {
                showDeleteConfirm(faq)
              }
            }}
          />
        </div>
        {isOnlyFaqInCategory && (
          <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '5px' }}>
            Cannot delete the only FAQ in this category
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (rawFaqs.length) {
      setCategories(groupFaqsByCategory(rawFaqs, activeLanguage))
    }
  }, [activeLanguage, rawFaqs])

  useEffect(() => {
    if (isModalVisible && currentFaq) {
      const titleField = `title_${modalActiveLanguage}`
      const descriptionField = `description_${modalActiveLanguage}`

      const titleValue =
        currentFaq.faq_translations?.[modalActiveLanguage]?.name || ''
      const descriptionValue =
        currentFaq.faq_translations?.[modalActiveLanguage]?.description || ''

      form.setFieldsValue({
        [titleField]: titleValue,
        [descriptionField]: descriptionValue,
      })
    }
  }, [modalActiveLanguage, isModalVisible, currentFaq, form])
  useEffect(() => {
    if (isCategoryModalVisible && editingCategory) {
      const nameField = `name_${categoryActiveLanguage}`
      const nameValue =
        editingCategory.faqs[0]?.category_translations?.[categoryActiveLanguage]
          ?.name || ''

      categoryForm.setFieldsValue({
        [nameField]: nameValue,
      })
    }
  }, [
    categoryActiveLanguage,
    isCategoryModalVisible,
    editingCategory,
    categoryForm,
  ])

  return (
    <Spin spinning={isLoading}>
      <div>
        <div>
          <Button onClick={() => showEditModal(null)} type="primary">
            <AppstoreAddOutlined />
            Add FAQ
          </Button>

          <Select
            value={activeLanguage}
            onChange={setActiveLanguage}
            style={{ marginLeft: 10 }}
          >
            {languages.map((lang) => (
              <Option value={lang} key={lang}>
                {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
              </Option>
            ))}
          </Select>
        </div>

        <Divider style={{ margin: '10px 0' }} />

        {categories.map((category) => (
          <div key={category.category} style={{ marginTop: 10 }}>
            {renderCategoryHeader(category)}
            <Collapse>
              {category.faqs.map((faq) => (
                <Panel header={renderFaqPanelHeader(faq)} key={faq.id}>
                  <div
                    style={{ color: 'white', marginLeft: 25 }}
                    dangerouslySetInnerHTML={{
                      __html:
                        faq.faq_translations?.[activeLanguage]?.description ||
                        'No description',
                    }}
                  />
                </Panel>
              ))}
            </Collapse>
          </div>
        ))}

        <Modal
          title={currentFaq ? 'Edit FAQ' : 'Add New FAQ'}
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          width={800}
          destroyOnClose
        >
          <Form form={form} layout="vertical" preserve={true}>
            <Form.Item name="id" style={{ display: 'none' }}>
              <Input type="hidden" />
            </Form.Item>

            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Category is required' }]}
              help={
                isCategoryDisabled
                  ? 'Cannot change category: This is the only FAQ in this category'
                  : null
              }
            >
              <Select
                disabled={isCategoryDisabled}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                placeholder="Select a category"
                style={{ width: 200 }}
              >
                {categories.map((cat) => (
                  <Option key={cat.category} value={cat.category}>
                    {cat.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Language">
              <Select
                value={modalActiveLanguage}
                onChange={setModalActiveLanguage}
                style={{ width: 200 }}
              >
                {languages.map((lang) => (
                  <Option key={lang} value={lang}>
                    {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name={`title_${modalActiveLanguage}`}
              label="Title"
              rules={[
                {
                  required: true,
                  message: 'Title is required',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={`description_${modalActiveLanguage}`}
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Description is required',
                },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Edit Category"
          visible={isCategoryModalVisible}
          onOk={handleCategoryOk}
          onCancel={() => setIsCategoryModalVisible(false)}
          width={600}
          destroyOnClose
        >
          <Form form={categoryForm} layout="vertical" preserve={true}>
            <Form.Item label="Language">
              <Select
                value={categoryActiveLanguage}
                onChange={setCategoryActiveLanguage}
                style={{ width: 200 }}
              >
                {languages.map((lang) => (
                  <Option key={lang} value={lang}>
                    {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name={`name_${categoryActiveLanguage}`}
              label="Category Name"
              rules={[
                {
                  required: true,
                  message: 'Category name is required',
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  )
}

export default Faq
